import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef } from "react";

export function ActivityAbandonDialog({
  discarding,
  onKeepEditing,
  onDiscard,
}: {
  discarding: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    keepButtonRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="activity-abandon-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        if (!discarding) onKeepEditing();
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <span className="section-kicker">UNSAVED ACTIVITY</span>
      <h2 id={headingId}>Discard unsaved changes?</h2>
      <p id={descriptionId}>
        Your changes to this activity haven’t been saved. If you leave now, they’ll be lost.
      </p>
      <div className="activity-abandon-actions">
        <button
          ref={keepButtonRef}
          type="button"
          className="soft-button"
          disabled={discarding}
          onClick={onKeepEditing}
        >
          Keep editing
        </button>
        <button type="button" className="danger-button" disabled={discarding} onClick={onDiscard}>
          Discard changes
        </button>
      </div>
    </dialog>
  );
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
